import { Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import _, { cloneDeep } from 'lodash';
import { nameof } from 'src/app/helper/nameof';
import { FeatureEnum } from 'src/app/samwebDescription/samwin/FeatureEnum';
import { GlobalConfigurationsState } from 'src/app/store/samwin/global-configurations/global-configurations.state';
import { SamwinAcdExtensionStateSelectors } from 'src/app/store/samwin/samwin-acd-extension/samwin-acd-extension.selectors';
import { SamwinAcdExtensionState } from 'src/app/store/samwin/samwin-acd-extension/samwin-acd-extension.state';
import { SamwinCallListState } from 'src/app/store/samwin/samwin-call-list/samwin-call-list.state';
import { SamwinLinesState } from 'src/app/store/samwin/samwin-lines/samwin-lines.state';
import { EnabledFeaturesState } from 'src/app/store/session/enabled-features/enabled-features.state';
import { SessionState, SessionUserInfo } from 'src/app/store/session/session.state';
import { SamwinCallInfoRemoteDto } from 'src/generated/signalR/samwin-call-info-remote-dto';
import { SkillConfigurationRemoteDto } from 'src/generated/signalR/skill-configuration-remote-dto';
import { SkillDiversionTargetRemoteDto } from 'src/generated/signalR/skill-diversion-target-remote-dto';
import { SkillRealtimeRemoteDto } from 'src/generated/signalR/skill-realtime-remote-dto';
import { SkillUserConfigurationRemoteDto } from 'src/generated/signalR/skill-user-configuration-remote-dto';
import { ChangeSkillStatusRequestData, SkillsService } from 'src/generated/swagger';
import { MailingFolderEnum } from './mailing-folders-enum';
import { MailingSkillTreeItem } from './mailing-skill-tree-item';
import { RequiredAgentForSkills } from './required-agents-dto';
import { SamwinSkillTypeEnum } from './samwin-skills-type-enum';
import {
   ChangeSkillStatus,
   PermittedSkillDiversionUpdated,
   RedirectSkill,
   RegisterForForeignSkillInfos,
   SkillConfigurationChanged,
   SkillRealtimeDataChanged,
   SkillUserConfigurationChanged,
} from './samwin-skills.actions';
import { Skill } from './skill';
import { SkillConstants } from './skill-constants';
import { SkillGroup } from './skill-group';

export class SkillLoadingInformation {
   constructor(public skillType: SamwinSkillTypeEnum, public shouldBeActive, public isLoading: boolean) {}
}

export interface SamwinSkillsStateModel {
   skillConfiguration: SkillConfigurationRemoteDto[];
   skillUserConfiguration: SkillUserConfigurationRemoteDto[];
   skillRealtimeData: SkillRealtimeRemoteDto[];
   permittedDiversions: SkillDiversionTargetRemoteDto[];
   skillSelectedByAgent: {
      skillId: number;
      agentId: number;
   }[];
   skillLoadingInformation: { [skillId: number]: SkillLoadingInformation };
}

@State<SamwinSkillsStateModel>({
   name: 'samwinSkillList',
   defaults: {
      skillConfiguration: [],
      skillUserConfiguration: [],
      skillRealtimeData: [],
      permittedDiversions: [],
      skillSelectedByAgent: null,
      skillLoadingInformation: {},
   },
})
@Injectable()
export class SamwinSkillsState {
   constructor(private skillsService: SkillsService, private store: Store) {}

   // TODO: refactor in the future?
   private static createRootSkill(id: number, translationKey: string, visibleSkills: _.Collection<SkillGroup>, userId: number) {
      let userIds: number[] = [];
      let allUserIds: number[] = [];

      let isInactive = false;
      let hasManageableSkills = false;
      let skillType: number = null;
      visibleSkills.forEach((skillGroup) => {
         skillGroup.skills.forEach((skill) => {
            if (!isInactive) {
               isInactive = !_.includes(skill.loggedOnUserIds, userId);
            }
            if (!hasManageableSkills && skill.skillUserConfiguration.isManageable) {
               hasManageableSkills = true;
            }
            if (_.isNil(skillType) && !_.isNil(skill?.skillConfiguration?.skillType)) {
               skillType = skill.skillConfiguration.skillType;
            }
            allUserIds = _.union(allUserIds, skill.loggedOnUserIds);
         });
         userIds = _.union(userIds, skillGroup.loggedOnUserIds);
      });

      if (isInactive) {
         if (_.includes(allUserIds, userId)) {
            _.remove(allUserIds, (usrId) => usrId === userId);
         }
      }

      return <Skill>{
         skillId: id,
         loggedOnUserIds: userIds,
         loggedOnToAllUserIds: allUserIds,
         oldestCallTimeStamp: visibleSkills.map((skill) => skill.oldestCallTimeStamp).min(),
         waitCount: visibleSkills.sumBy((skill) => skill.waitCount),
         redirectionAddress: '',
         skillConfiguration: {
            id: id,
            skillColor: '',
            skillName: translationKey,
            originName: translationKey,
            skillType: skillType,
         },
         skillUserConfiguration: {
            id: id,
            userSkillValue: 0,
            isManageable: hasManageableSkills,
            isActive: true,
            skillType: skillType,
         },
      };
   }

   @Selector()
   public static createSkillFromConfig() {
      return (
         skillConfiguration: SkillConfigurationRemoteDto[],
         skillUserConfiguration: SkillUserConfigurationRemoteDto[],
         skillRealtimeData: SkillRealtimeRemoteDto[]
      ) => {
         return skillConfiguration.map(
            (config) =>
               new Skill(
                  config,
                  skillUserConfiguration.find((userConfig) => userConfig.id === config.id),
                  skillRealtimeData.find((realtime) => realtime.skillId === config.id)
               )
         );
      };
   }

   @Selector()
   public static createGroupFromSkills() {
      return (skills: Skill[]) => {
         const groupedSkills = _(skills)
            .groupBy((skill) => skill.skillConfiguration?.skillName ?? SkillConstants.UNKNOWN_SKILL_NAME)
            .map((skill) => new SkillGroup(skill))
            .orderBy((skill) => skill.skillConfiguration?.skillName.toLowerCase() ?? SkillConstants.UNKNOWN_SKILL_NAME);
         return groupedSkills.value();
      };
   }

   @Selector()
   public static getSkillsFromGroup() {
      return (skillGroups: SkillGroup[]) => {
         return _(skillGroups)
            .mapValues(nameof<SkillGroup>('skills'))
            .flatMap()
            .map((skill) => <Skill>(<any>skill))
            .value();
      };
   }

   @Selector([SessionState.getAuthenticatedUserId])
   public static getRootSkill(userId: number) {
      return (translationKey: string, visibleSkills: _.Collection<SkillGroup>) => {
         return SamwinSkillsState.createRootSkill(SkillConstants.OVERALL_SKILL_ID, translationKey, visibleSkills, userId);
      };
   }

   @Selector([SamwinSkillsState.getSkillConfigurationFn])
   public static getSkillDefaultTemplate(skillConfiguration: (skillId: number) => SkillConfigurationRemoteDto) {
      return (skillId: number) => {
         return skillConfiguration(skillId)?.defaultTemplateId;
      };
   }

   @Selector([SamwinSkillsState.getSkillDefaultTemplate, SamwinAcdExtensionStateSelectors.getCurrentAcdSkillID, GlobalConfigurationsState.defaultTemplateId])
   public static getDefaultTemplateID(skillDefaultTemplateId: (skillId: number) => number, acdSkillId: number, defaultTemplateId: number) {
      return (skillId: number) => {
         let skillTemplateId = skillDefaultTemplateId(skillId);

         if (!_.isNil(skillTemplateId) && skillTemplateId > 0) {
            return skillTemplateId;
         }
         skillTemplateId = skillDefaultTemplateId(acdSkillId);

         if (!_.isNil(skillTemplateId) && skillTemplateId > 0) {
            return skillTemplateId;
         }
         return defaultTemplateId;
      };
   }

   @Selector([SamwinSkillsState])
   public static getSkillConfiguration(state: SamwinSkillsStateModel): SkillConfigurationRemoteDto[] {
      return state?.skillConfiguration ?? [];
   }

   @Selector([SamwinSkillsState])
   public static getSkillUserConfiguration(state: SamwinSkillsStateModel): SkillUserConfigurationRemoteDto[] {
      return state?.skillUserConfiguration ?? [];
   }

   @Selector([SamwinSkillsState])
   public static getSkillRealtimeData(state: SamwinSkillsStateModel): SkillRealtimeRemoteDto[] {
      return state?.skillRealtimeData ?? [];
   }

   @Selector([SamwinSkillsState])
   public static skillLoadingInformation(state: SamwinSkillsStateModel) {
      return state?.skillLoadingInformation;
   }

   @Selector([SamwinSkillsState])
   public static skillConfigurationsForWaitingItems(state: SamwinSkillsStateModel): SkillConfigurationRemoteDto[] {
      return state.skillConfiguration.filter((skill) => skill.informWaitingItem);
   }

   @Selector([SamwinSkillsState.getSkillUserConfiguration])
   public static visibleSkillIds(skillUserConfiguration: SkillUserConfigurationRemoteDto[]): number[] {
      return skillUserConfiguration
         .filter((userConfig) => userConfig?.isActive || userConfig?.isActive === undefined || (userConfig?.isManageable && userConfig?.userSkillValue > 0))
         .map((userConfig) => userConfig.id);
   }

   @Selector([SamwinSkillsState.skillLoadingInformation])
   public static getLoadingSkillIds(skillLoadingInformation: { [skillId: number]: SkillLoadingInformation }): (skillIds: number[]) => number[] {
      return (skillIds: number[]) => {
         return skillIds.filter((id) => skillLoadingInformation[id]?.isLoading);
      };
   }

   @Selector([SamwinSkillsState.visibleSkillIds, SamwinSkillsState.getLoadingSkillIds])
   public static areVisibleSkillsLoading(visibleSkillIds: number[], getLoadingSkillIds: (skillIds: number[]) => number[]): boolean {
      return !_.isEmpty(_.intersection(visibleSkillIds, getLoadingSkillIds(visibleSkillIds)));
   }

   @Selector([SamwinSkillsState.getSkillConfiguration, SamwinSkillsState.visibleSkillIds])
   public static getVisibleSkillConfiguration(skillConfiguration: SkillConfigurationRemoteDto[], visibleIds: number[]): SkillConfigurationRemoteDto[] {
      return skillConfiguration.filter((config) => visibleIds.includes(config?.id));
   }

   @Selector([SamwinSkillsState.getSkillUserConfiguration, SamwinSkillsState.visibleSkillIds])
   public static getVisibleSkillUserConfiguration(
      skillUserConfiguration: SkillUserConfigurationRemoteDto[],
      visibleIds: number[]
   ): SkillUserConfigurationRemoteDto[] {
      return skillUserConfiguration.filter((userconfig) => visibleIds.includes(userconfig?.id));
   }

   @Selector([SamwinSkillsState.getSkillRealtimeData, SamwinSkillsState.visibleSkillIds])
   public static getVisibleSkillRealtimeData(skillRealtimeData: SkillRealtimeRemoteDto[], visibleIds: number[]): SkillRealtimeRemoteDto[] {
      return skillRealtimeData.filter((realtimeData) => visibleIds.includes(realtimeData?.skillId));
   }

   @Selector([
      SamwinSkillsState.getVisibleSkillConfiguration,
      SamwinSkillsState.getVisibleSkillUserConfiguration,
      SamwinSkillsState.getVisibleSkillRealtimeData,
      SamwinSkillsState.createSkillFromConfig,
   ])
   public static getVisibleSkills(
      skillConfiguration: SkillConfigurationRemoteDto[],
      skillUserConfiguration: SkillUserConfigurationRemoteDto[],
      skillRealtimeData: SkillRealtimeRemoteDto[],
      createSkillFromConfig: (
         skillConfiguration: SkillConfigurationRemoteDto[],
         skillUserConfiguration: SkillUserConfigurationRemoteDto[],
         skillRealtimeData: SkillRealtimeRemoteDto[]
      ) => Skill[]
   ) {
      if (_.isEmpty(skillRealtimeData)) {
         return [];
      }
      return createSkillFromConfig(skillConfiguration, skillUserConfiguration, skillRealtimeData);
   }

   @Selector([SamwinSkillsState.getVisibleSkills, SamwinSkillsState.createGroupFromSkills])
   public static getVisibleSkillGroup(visibleSkills: Skill[], createGroupFromSkills: (skills: Skill[]) => SkillGroup[]) {
      return createGroupFromSkills(visibleSkills);
   }

   @Selector([SamwinSkillsState.getVisibleSkillGroup, SamwinSkillsState.getRootSkill])
   public static getVisibleSkillGroupsInclusiveOverallSkills(
      visibleSkillGroups: SkillGroup[],
      getRootSkill: (translationKey: string, visibleSkills: _.Collection<SkillGroup>) => Skill
   ) {
      return _(visibleSkillGroups)
         .unshift(new SkillGroup([getRootSkill('brick.brick_definitions.skill-browser.root_skill_name', _(visibleSkillGroups))]))
         .value();
   }

   @Selector([SamwinSkillsState.getVisibleSkillGroupsInclusiveOverallSkills])
   public static getSkillGroup(skillGroups: SkillGroup[]) {
      return (skillId: number) => {
         return skillGroups.find((x) => x?.skillId === skillId);
      };
   }

   @Selector([SamwinSkillsState.getSkillUserConfiguration])
   public static isSkillActiveOnStart(skillUserConfiguration: SkillUserConfigurationRemoteDto[]) {
      return (skillId: number) => {
         return skillUserConfiguration.find((userConf) => userConf.id === skillId)?.isActive ?? false;
      };
   }

   @Selector([SamwinSkillsState])
   public static isSkillLoading(state: SamwinSkillsStateModel): (skillId: number) => boolean {
      return (skillId: number) => {
         return state.skillLoadingInformation[skillId]?.isLoading ?? false;
      };
   }

   @Selector([SamwinSkillsState])
   public static isAllSkillLoading(state: SamwinSkillsStateModel): (skillType: SamwinSkillTypeEnum) => boolean {
      return (skillType: SamwinSkillTypeEnum) => {
         return _(state.skillLoadingInformation)
            .filter((loadingInfo) => loadingInfo.skillType.value === skillType.value)
            .some((loadingInfo) => loadingInfo.isLoading);
      };
   }

   @Selector([SamwinSkillsState.getSkillConfiguration])
   public static getCallAndChatSkills(skills: SkillConfigurationRemoteDto[]): SkillConfigurationRemoteDto[] {
      return _(skills)
         .filter((skill) => (skill.skillType ?? -1) === SamwinSkillTypeEnum.CALLING.value)
         .value();
   }

   @Selector([SamwinSkillsState.getCallAndChatSkills, SamwinSkillsState.getSkillRealtimeData, SessionState.getAuthenticatedUserId])
   public static getLoggedOnCallAndChatSkillIds(skills: SkillConfigurationRemoteDto[], skillRealtimeData: SkillRealtimeRemoteDto[], userId: number): number[] {
      return _(skills)
         .filter((skill) => skillRealtimeData.find((realtime) => realtime.skillId === skill.id)?.loggedOnUserIds.includes(userId))
         .map((skill) => skill.id)
         .value();
   }

   @Selector([SamwinSkillsState.getSkillConfiguration])
   public static getMailingSkills(skills: SkillConfigurationRemoteDto[]): SkillConfigurationRemoteDto[] {
      return _(skills)
         .filter((skill) => (skill.skillType ?? -1) === SamwinSkillTypeEnum.MAILING.value)
         .value();
   }

   @Selector([SamwinSkillsState.getSkillUserConfiguration])
   public static getIsAccessibleMailingSkill(skillUserConfiguration: SkillUserConfigurationRemoteDto[]): (skillId: number) => boolean {
      return (skillId: number) => {
         return _(skillUserConfiguration).find((skill) => skill.id === skillId && (skill.skillType ?? -1) === SamwinSkillTypeEnum.MAILING.value).isAccessible;
      };
   }

   @Selector([SamwinSkillsState.getCallAndChatSkills, SamwinSkillsState.getSkillUserConfiguration, SamwinSkillsState.getSkillRealtimeData])
   public static getCallAndChatSkillGroups(
      skillConfiguration: SkillConfigurationRemoteDto[],
      skillUserConfiguration: SkillUserConfigurationRemoteDto[],
      skillRealtimeData: SkillRealtimeRemoteDto[]
   ) {
      const skillio = skillConfiguration.map((config) => {
         return new Skill(
            config,
            skillUserConfiguration.find((userconfig) => userconfig.id === config.id),
            skillRealtimeData.find((realtime) => realtime.skillId === config.id)
         );
      });
      const visibleSkills = _(skillio).filter(
         (skill) =>
            (skill?.skillUserConfiguration?.isActive || skill?.skillUserConfiguration?.isActive === undefined || skill?.skillUserConfiguration?.isManageable) &&
            skill?.skillUserConfiguration?.userSkillValue > 0
      );

      const groupedSkills = visibleSkills
         .groupBy((skill) => skill.skillConfiguration?.skillName ?? SkillConstants.UNKNOWN_SKILL_NAME)
         .map((skill) => new SkillGroup(skill))
         .orderBy((skill) => skill.skillConfiguration?.skillName.toLowerCase() ?? SkillConstants.UNKNOWN_SKILL_NAME);
      return groupedSkills.value();
   }

   @Selector([SamwinSkillsState.getMailingSkills, SamwinSkillsState.getSkillUserConfiguration, SamwinSkillsState.getSkillRealtimeData])
   public static getMailingSkillGroups(
      skillConfiguration: SkillConfigurationRemoteDto[],
      skillUserConfiguration: SkillUserConfigurationRemoteDto[],
      skillRealtimeData: SkillRealtimeRemoteDto[]
   ) {
      const skillio = skillConfiguration.map((config) => {
         return new Skill(
            config,
            skillUserConfiguration.find((userconfig) => userconfig.id === config.id),
            skillRealtimeData.find((realtime) => realtime.skillId === config.id)
         );
      });

      const visibleSkills = _(skillio).filter(
         (skill) =>
            (skill?.skillUserConfiguration?.isActive || skill?.skillUserConfiguration?.isActive === undefined || skill?.skillUserConfiguration?.isManageable) &&
            skill?.skillUserConfiguration?.userSkillValue > 0
      );
      const groupedSkills = visibleSkills
         .groupBy((skill) => skill.skillConfiguration?.skillName ?? SkillConstants.UNKNOWN_SKILL_NAME)
         .map((skill) => new SkillGroup(skill))
         .orderBy((skill) => skill.skillConfiguration?.skillName.toLowerCase() ?? SkillConstants.UNKNOWN_SKILL_NAME);
      return groupedSkills.value();
   }

   @Selector([SamwinSkillsState.getCallAndChatSkillGroups, SamwinSkillsState.getMailingSkillGroups])
   public static getSkillGroups(callAndChatSkills: SkillGroup[], MailingSkills: SkillGroup[]): SkillGroup[] {
      return _.concat(callAndChatSkills, MailingSkills);
   }

   @Selector([SamwinSkillsState.getSkillConfiguration, SamwinSkillsState.getSkillUserConfiguration, SamwinSkillsState.getSkillRealtimeData])
   public static getSkillWithIdFn(
      skillConfiguration: SkillConfigurationRemoteDto[],
      skillUserConfiguration: SkillUserConfigurationRemoteDto[],
      skillRealtimeData: SkillRealtimeRemoteDto[]
   ): (skillId: number) => Skill {
      return (skillId: number) => {
         return new Skill(
            skillConfiguration.find((config) => config.id === skillId),
            skillUserConfiguration.find((userconfig) => userconfig.id === skillId),
            skillRealtimeData.find((realtime) => realtime.skillId === skillId)
         );
      };
   }

   @Selector([SamwinSkillsState.getSkillUserConfiguration])
   public static getSkillUserConfigurationWithIdFn(
      skillUserConfiguration: SkillUserConfigurationRemoteDto[]
   ): (skillId: number) => SkillUserConfigurationRemoteDto {
      return (skillId: number) => {
         return skillUserConfiguration.find((userconfig) => userconfig.id === skillId);
      };
   }

   public static getSkill(skillId: number): (skillConfiguration, skillUserConfiguration, skillRealtimeData) => Skill {
      return createSelector(
         [SamwinSkillsState.getSkillConfiguration, SamwinSkillsState.getSkillUserConfiguration, SamwinSkillsState.getSkillRealtimeData],
         (
            skillConfiguration: SkillConfigurationRemoteDto[],
            skillUserConfiguration: SkillUserConfigurationRemoteDto[],
            skillRealtimeData: SkillRealtimeRemoteDto[]
         ) => {
            return new Skill(
               skillConfiguration.find((config) => config.id === skillId),
               skillUserConfiguration.find((userconfig) => userconfig.id === skillId),
               skillRealtimeData.find((realtime) => realtime.skillId === skillId)
            );
         }
      );
   }

   public static getCallSkill(skillId: number): (skillConfiguration, skillUserConfiguration, skillRealtimeData) => Skill {
      return createSelector(
         [SamwinSkillsState.getCallAndChatSkills, SamwinSkillsState.getSkillUserConfiguration, SamwinSkillsState.getSkillRealtimeData],
         (
            skillConfiguration: SkillConfigurationRemoteDto[],
            skillUserConfiguration: SkillUserConfigurationRemoteDto[],
            skillRealtimeData: SkillRealtimeRemoteDto[]
         ) => {
            return new Skill(
               skillConfiguration.find((config) => config.id === skillId),
               skillUserConfiguration.find((userconfig) => userconfig.id === skillId),
               skillRealtimeData.find((realtime) => realtime.skillId === skillId)
            );
         }
      );
   }

   @Selector([SamwinSkillsState.getSkillGroups])
   public static getSkillGroupById(skills: SkillGroup[]): (skillId: number) => SkillGroup {
      return (skillId: number) => {
         return _(skills)
            .filter(
               (skill) =>
                  !_.isNil(
                     _(skill.skills)
                        .filter((subskill) => subskill.skillId === skillId)
                        .head()
                  )
            )
            .head();
      };
   }

   @Selector([SamwinSkillsState])
   public static getPermittedSkillDiversions(state: SamwinSkillsStateModel): SkillDiversionTargetRemoteDto[] {
      return state.permittedDiversions;
   }

   @Selector([SamwinSkillsState])
   public static getIsSharedSkillFn(state: SamwinSkillsStateModel) {
      return (skillId: number) => {
         const skill = _(state.skillConfiguration)
            .filter((x) => x.id === skillId)
            .head();
         if (_.isNil(skill)) {
            return false;
         }
         return skill.isShared;
      };
   }

   @Selector([SamwinSkillsState.getSkillConfiguration])
   public static getSkillConfigurationFn(skillConfiguration: SkillConfigurationRemoteDto[]): (skillId: number) => SkillConfigurationRemoteDto {
      return (skillId: number) => {
         return skillConfiguration.find((x) => x.id === skillId);
      };
   }

   @Selector([SamwinLinesState.getActiveOrRingingCallId, SamwinCallListState.getCallByIdFn, SamwinSkillsState.getSkillConfigurationFn])
   public static getSkillConfigurationOfCurrentCall(
      activeCallId: number,
      getCallByIdFn: (callId: number) => SamwinCallInfoRemoteDto,
      getSkillConfigurationFn: (skillId: number) => SkillConfigurationRemoteDto
   ) {
      if (_.isNil(activeCallId)) return null;

      const callInfo = getCallByIdFn(activeCallId);
      if (_.isNil(callInfo)) return null;

      return getSkillConfigurationFn(callInfo.queueId);
   }

   @Selector([SamwinLinesState.getActiveOrRingingCallId, SamwinCallListState.getCallByIdFn])
   public static isCurrentCallInPrivateSkill(activeCallId: number, getCallByIdFn: (callId: number) => SamwinCallInfoRemoteDto) {
      if (_.isNil(activeCallId)) return false;

      const callInfo = getCallByIdFn(activeCallId);
      if (_.isNil(callInfo)) return false;

      return callInfo.queueId < 0;
   }

   @Selector([SamwinSkillsState.getSkillRealtimeData])
   public static isUserLoggedOnToSkill(skillRealtimeData: SkillRealtimeRemoteDto[]): (skillId: number, userId: number) => boolean {
      return (skillId: number, userId: number) => {
         return !_.isNil(skillRealtimeData.find((x) => x.skillId === skillId && x.loggedOnUserIds.includes(userId)));
      };
   }

   public static getIsSkillDiversionAllowedForSkill(
      skillId: number
   ): (
      skillDiversions: SkillDiversionTargetRemoteDto[],
      isFreelyDivertEnabled: boolean,
      skillState: Skill,
      userId: number,
      isTheOnlyLoggedOnUserOnAnySkill: boolean
   ) => boolean {
      return createSelector(
         [
            SamwinSkillsState.getPermittedSkillDiversions,
            EnabledFeaturesState.isFeatureEnabled(FeatureEnum.CCO_PERM_MAY_FREELY_DIVERT_SKILL),
            SamwinSkillsState.getCallSkill(skillId),
            SessionState.getAuthenticatedUserId,
            SamwinSkillsState.getIsTheOnlyLoggedOnUserOnAnySkill,
         ],
         (
            skillDiversions: SkillDiversionTargetRemoteDto[],
            mayFreelyDivertSkill: boolean,
            skillState: Skill,
            userId: number,
            isTheOnlyLoggedOnUserOnAnySkill: boolean
         ) => {
            // Setting a forward is not possible when
            //  - the agent is not allowed to freely configure a forwarding
            //  - and there are no predefined forwards
            if (skillDiversions.length === 0 && !mayFreelyDivertSkill) {
               return false;
            }

            if (skillId === SkillConstants.OVERALL_SKILL_ID) {
               // setting root forward is possible if setting forward is possible for any of the other skills
               return isTheOnlyLoggedOnUserOnAnySkill;
            }

            // setting a forward is only possible if the agent is the only logged on user
            return (
               !_.isNil(skillState) &&
               skillState.skillUserConfiguration.isManageable &&
               skillState.loggedOnUserIds?.length === 1 &&
               _.includes(skillState.loggedOnUserIds, userId)
            );
         }
      );
   }

   @Selector([SamwinSkillsState.getSkillUserConfiguration, SamwinSkillsState.getSkillRealtimeData, SessionState.getAuthenticatedUserId])
   public static getIsTheOnlyLoggedOnUserOnAnySkill(
      skillUserConfiguration: SkillUserConfigurationRemoteDto[],
      skillRealtimeData: SkillRealtimeRemoteDto[],
      authenticatedUserId: number
   ): boolean {
      return (
         !_.isNil(authenticatedUserId) &&
         _.some(
            skillUserConfiguration,
            (s) =>
               s?.isManageable &&
               skillRealtimeData.find((x) => x.skillId === s.id && x.loggedOnUserIds.includes(authenticatedUserId) && x.loggedOnUserIds.length === 1)
         )
      );
   }

   @Selector([SamwinSkillsState])
   public static getCurrentAgentState(state: SamwinSkillsStateModel) {
      return state.skillSelectedByAgent;
   }

   @Selector([SamwinSkillsState.getCallAndChatSkillGroups])
   public static getCallGroupedSkills(skills: SkillGroup[]) {
      return _(skills)
         .filter(
            (skill) =>
               (skill.skillUserConfiguration.isActive || skill.skillUserConfiguration.isActive === undefined || skill.skillUserConfiguration.isManageable) &&
               skill.skillUserConfiguration.userSkillValue > 0
         )
         .value();
   }

   @Selector([SamwinSkillsState.getCallGroupedSkills, SamwinSkillsState.getRootSkill])
   public static callSkillsInclusiveOverallSkill(
      groupedSkills: SkillGroup[],
      getRootSkill: (translationKey: string, visibleSkills: _.Collection<SkillGroup>) => Skill
   ) {
      return _(groupedSkills)
         .unshift(new SkillGroup([getRootSkill('brick.brick_definitions.skill-browser.root_skill_name', _(groupedSkills))]))
         .value();
   }

   @Selector([SamwinSkillsState.getMailingSkillGroups])
   public static getMailingSkillTree(skills: SkillGroup[]) {
      const groupedSkills = _(skills).filter(
         (skill) =>
            (skill.skillUserConfiguration.isActive || skill.skillUserConfiguration.isActive === undefined || skill.skillUserConfiguration.isManageable) &&
            skill.skillUserConfiguration.userSkillValue > 0
      );
      const result: MailingSkillTreeItem[] = [];
      MailingFolderEnum.values().forEach((item: MailingFolderEnum) => {
         const skillTreeItems = groupedSkills
            .map(
               (skill) =>
                  new MailingSkillTreeItem(
                     skill.skillId,
                     skill.skillConfiguration.skillName,
                     item.name,
                     [],
                     null,
                     item.name === MailingFolderEnum.INBOX.name ? true : false
                  )
            )
            .value();
         // skill id of root tree note must be null, for filtering
         result.push(new MailingSkillTreeItem(null, '', item.name, skillTreeItems, item.icon, item.name === MailingFolderEnum.INBOX.name ? true : false));
      });
      return result;
   }

   @Selector([SamwinSkillsState.getMailingSkillTree])
   public static getMailingSkillTreeChildren(mailingSkillTreeItems: MailingSkillTreeItem[]) {
      return (groupKey: string) => {
         return mailingSkillTreeItems.find((skillItem) => skillItem.groupKey === groupKey).children;
      };
   }

   @Selector([SamwinSkillsState.getMailingSkillGroups, SamwinSkillsState.getRootSkill])
   public static getMailingSkillById(skills: SkillGroup[], getRootSkill: (translationKey: string, visibleSkills: _.Collection<SkillGroup>) => Skill) {
      return (id: number, groupName: string) => {
         const groupedSkills = _(skills).filter(
            (skill) =>
               (skill.skillUserConfiguration.isActive || skill.skillUserConfiguration.isActive === undefined || skill.skillUserConfiguration.isManageable) &&
               skill.skillUserConfiguration.userSkillValue > 0
         );
         if (id <= SkillConstants.OVERALL_SKILL_ID) {
            const header = new SkillGroup([getRootSkill(MailingFolderEnum[groupName].translation, groupedSkills)]);
            return header;
         }
         const result = groupedSkills.filter((skill) => skill.skillId === id).head();
         return result;
      };
   }

   @Selector([
      SamwinAcdExtensionState.getAssignedAcdExtension,
      SamwinSkillsState.isSkillActiveOnStart,
      EnabledFeaturesState.isFeatureEnabled(FeatureEnum.DO_SHOW_REQUIRED_AGENT_WARNING_FOR_ACTIVE_SKILLS),
      EnabledFeaturesState.isFeatureEnabled(FeatureEnum.DO_SHOW_REQUIRED_AGENT_WARNING_FOR_MANAGEABLE_SKILLS),
      EnabledFeaturesState.isFeatureEnabled(FeatureEnum.DO_SHOW_REQUIRED_AGENT_WARNING_TRIGGERED_BY_OTHER_USERS),
      SamwinSkillsState.getCurrentAgentState,
      SamwinSkillsState.getCallAndChatSkillGroups,
   ])
   public static getSkillsRequiringAgents(
      agentId: number,
      isSkillActiveOnStart: (skillId: number) => boolean,
      isShowRequiredAgentWarningForActiveSkillsEnabled: boolean,
      isShowRequiredAgentWarningForManageableSkillsEnabled: boolean,
      canOtherUsersTriggerRequiredAgentNotification: boolean,
      agentState: {
         skillId: number;
         agentId: number;
      }[],
      skillGroup: SkillGroup[]
   ) {
      return (skills: Skill[], withoutOwnAgent: boolean) => {
         const agentArray = _(skills)
            .filter((skill) => {
               if (_.isNil(agentId)) {
                  return false;
               }
               const correspondingSkillGroup = skillGroup.find((entry) => entry.skillId === skill.skillId);
               let activeAgentIds = correspondingSkillGroup?.activeAgentIds || skill?.activeAgentIds;
               if (withoutOwnAgent) {
                  activeAgentIds = _.difference(activeAgentIds, [agentId]);
               }
               const activeUserCount: number = activeAgentIds?.length;
               const isSkillActiveOnStartup: boolean = isSkillActiveOnStart(skill?.skillId);

               let requireMore = activeUserCount < skill.requiredAgentCount;
               if (!withoutOwnAgent && !canOtherUsersTriggerRequiredAgentNotification) {
                  const currentState = _(agentState).find((state) => state.skillId === skill.skillId);
                  const isSetByCurrentAgent: boolean = !_.isNil(currentState) && currentState.agentId === agentId;
                  const isInActive = !_(skill.activeAgentIds).includes(agentId);
                  requireMore = requireMore && !isSetByCurrentAgent && isInActive;
               }
               if (isShowRequiredAgentWarningForActiveSkillsEnabled && isSkillActiveOnStartup && requireMore) {
                  return true;
               }
               if (isShowRequiredAgentWarningForManageableSkillsEnabled && skill.skillUserConfiguration.isManageable && requireMore) {
                  return true;
               }
               return false;
            })
            .groupBy((skill) => skill.skillConfiguration.skillName)
            .map((skill) => new RequiredAgentForSkills(skill))
            .orderBy((skill) => skill.skillName.toLowerCase())
            .value();
         return agentArray;
      };
   }

   @Selector([SamwinSkillsState.getVisibleSkills, SamwinSkillsState.getSkillsRequiringAgents])
   public static understaffedSkills(skills: Skill[], getRequiredAgents: (skill: Skill[], withoutOwnAgent: boolean) => RequiredAgentForSkills[]) {
      return (withoutOwnAgent: boolean) => {
         return getRequiredAgents(skills, withoutOwnAgent);
      };
   }

   public static getUnderstaffedSkills(withoutOwnAgent: boolean) {
      return createSelector([SamwinSkillsState.understaffedSkills], (understaffedSkills: (withoutOwnAgent: boolean) => RequiredAgentForSkills[]) => {
         return understaffedSkills(withoutOwnAgent);
      });
   }

   @Selector([SessionState.getAuthenticatedUser, SamwinSkillsState.getSkillConfigurationFn])
   public static hasCurrentUserEmailConnector(user: SessionUserInfo, getSkillConfig: (skillId: number) => SkillConfigurationRemoteDto) {
      return (skillId: number) => {
         if (user?.emailConnectorId > 0 || getSkillConfig(skillId)?.emailConnectorId > 0) {
            return true;
         }
         return false;
      };
   }

   @Selector([SamwinCallListState.getCallByIdFn, SamwinCallListState.getCallInfoFromPreviousCall, SamwinSkillsState.getSkillConfigurationFn])
   public static getSkillConfigurationForCall(
      callFn: (callId: number) => null | SamwinCallInfoRemoteDto,
      previousCallInfo: SamwinCallInfoRemoteDto,
      getSkillConfigurationFn: (skillId: number) => SkillConfigurationRemoteDto
   ): (callId: number) => null | SkillConfigurationRemoteDto {
      return (callId: number) => {
         let callInfo: SamwinCallInfoRemoteDto;
         if (!_.isNil(callId)) {
            callInfo = callFn(callId);
         }
         if (_.isNil(callInfo)) {
            callInfo = previousCallInfo;
         }
         if (_.isNil(callInfo)) {
            return;
         }
         return getSkillConfigurationFn(callInfo.queueId);
      };
   }

   public getSkillSelectedByAgent(
      agentId: number,
      oldAgentState: { skillId: number; agentId: number }[],
      oldSkills: Skill[],
      newSkills: Skill[]
   ): { skillId: number; agentId: number }[] {
      const newAgentState: {
         skillId: number;
         agentId: number;
      }[] = _(newSkills)
         .map((newSkill) => {
            if (_.isEmpty(oldAgentState)) {
               return { skillId: newSkill.skillId, agentId: agentId };
            }
            const oldSkill = _(oldSkills).find((currAgent) => currAgent.skillId === newSkill.skillId);
            const oldSkillAgentState = _(oldAgentState).find((state) => state.skillId === newSkill.skillId);
            // get different Agent IDs
            let activationId: number;
            let deactivationId: number;
            const diffDeactivation: number[] = _.differenceWith(oldSkill?.activeAgentIds ?? [], newSkill.activeAgentIds, _.isEqual);
            const diffActivation: number[] = _.differenceWith(newSkill.activeAgentIds, oldSkill?.activeAgentIds ?? [], _.isEqual);
            if (!_.isEmpty(diffDeactivation)) {
               deactivationId = _(diffDeactivation).head();
            }
            if (!_.isEmpty(diffActivation)) {
               activationId = _(diffActivation).head();
            }
            // Set new Agent IDs on change
            const requireAgents = _(oldSkill?.activeAgentIds ?? []).value().length < oldSkill?.requiredAgentCount;
            if (!_.isEmpty(oldSkillAgentState) && deactivationId > 0 && deactivationId !== oldSkillAgentState.agentId) {
               // check if required agents is triggered by own Agent
               if (!requireAgents && deactivationId !== agentId) {
                  return { skillId: newSkill.skillId, agentId: deactivationId };
               }
               if (deactivationId === agentId) {
                  return { skillId: newSkill.skillId, agentId: deactivationId };
               }
            }
            if (!_.isEmpty(oldSkillAgentState) && activationId > 0 && activationId !== oldSkillAgentState.agentId) {
               // check if required agents is triggered by own Agent
               if (!requireAgents && activationId !== agentId) {
                  return { skillId: newSkill.skillId, agentId: activationId };
               }
               if (activationId === agentId) {
                  return { skillId: newSkill.skillId, agentId: activationId };
               }
            }
            // Set Old State if Agent was not changed.
            if (!_.isEmpty(oldSkillAgentState)) {
               return { skillId: oldSkillAgentState.skillId, agentId: oldSkillAgentState.agentId };
            }
            // Set default state if empty.
            return { skillId: newSkill.skillId, agentId: -1 };
         })
         .value();
      return newAgentState;
   }

   @Action(SkillRealtimeDataChanged)
   public skillListChange(ctx: StateContext<SamwinSkillsStateModel>, action: SkillRealtimeDataChanged) {
      const userId: number = this.store.selectSnapshot(SessionState.getAuthenticatedUserId);
      ctx.patchState({
         skillRealtimeData: action.skillRealtimeData,
      });

      const state = ctx.getState();
      const skillsLoading = cloneDeep(state.skillLoadingInformation);
      action.skillRealtimeData
         .map((skills) => skills)
         .forEach((skill) => {
            if (
               _.isNil(skillsLoading[skill.skillId]?.shouldBeActive) ||
               skillsLoading[skill.skillId].shouldBeActive === skill.loggedOnUserIds.includes(userId)
            ) {
               skillsLoading[skill.skillId] = new SkillLoadingInformation(
                  SamwinSkillTypeEnum.valueOf(skill.skillType),
                  skill.loggedOnUserIds.includes(userId),
                  false
               );
            }
         });
      ctx.patchState({
         skillLoadingInformation: skillsLoading,
      });
   }

   @Action(SkillConfigurationChanged)
   public skillConfigurationChange(ctx: StateContext<SamwinSkillsStateModel>, action: SkillConfigurationChanged) {
      ctx.patchState({
         skillConfiguration: action.skillConfiguration,
      });
   }

   @Action(SkillUserConfigurationChanged)
   public skillUserConfigurationChange(ctx: StateContext<SamwinSkillsStateModel>, action: SkillUserConfigurationChanged) {
      ctx.patchState({
         skillUserConfiguration: action.skillUserConfiguration,
      });
   }

   @Action(ChangeSkillStatus)
   public updateSkillLoadingInformation(ctx: StateContext<SamwinSkillsStateModel>, action: ChangeSkillStatus) {
      if (_.isNil(action?.brickInstanceId)) {
         return;
      }
      const state = ctx.getState();
      const skillsLoading = cloneDeep(state.skillLoadingInformation);

      if (action.skillId === SkillConstants.OVERALL_SKILL_ID) {
         state.skillUserConfiguration
            .filter((skill) => skill.isManageable && skill.skillType === action.skillType.value && skill.userSkillValue > 0)
            .forEach((skill) => {
               skillsLoading[skill.id] = new SkillLoadingInformation(action.skillType, action.shouldSkillBeActive, true);
            });
      } else {
         skillsLoading[action.skillId] = new SkillLoadingInformation(action.skillType, action.shouldSkillBeActive, true);
      }
      ctx.patchState({
         skillLoadingInformation: skillsLoading,
      });
   }

   @Action(ChangeSkillStatus)
   public changeSkillStatus(ctx: StateContext<SamwinSkillsStateModel>, action: ChangeSkillStatus) {
      if (action.skillId === SkillConstants.OVERALL_SKILL_ID) {
         return this.skillsService.changeSkillStatusForAllSkills({
            skillShouldBeActive: action.shouldSkillBeActive,
            skillType: action.skillType.value,
         });
      }

      return this.skillsService.changeSkillStatus({ skillId: action.skillId, skillShouldBeActive: action.shouldSkillBeActive });
   }

   @Action(RegisterForForeignSkillInfos)
   public registerForForeignSkillInfos(ctx: StateContext<SamwinSkillsStateModel>, action: RegisterForForeignSkillInfos) {
      return this.skillsService.registerForForeignSkillInfos({ skillIds: action.skillIds });
   }

   @Action(RedirectSkill)
   public redirectSkill(ctx: StateContext<SamwinSkillsStateModel>, action: RedirectSkill) {
      if (action.skillId === SkillConstants.OVERALL_SKILL_ID) {
         return this.skillsService.changeSkillStatusForAllSkills({ skillShouldBeActive: false, redirectionTarget: action.diversionAddress });
      }
      return this.skillsService.changeSkillStatus(<ChangeSkillStatusRequestData>{
         skillId: action.skillId,
         skillShouldBeActive: false,
         redirectionTarget: action.diversionAddress,
      });
   }

   @Action(PermittedSkillDiversionUpdated)
   public permittedSkillDiversionUpdated(ctx: StateContext<SamwinSkillsStateModel>, action: PermittedSkillDiversionUpdated) {
      ctx.patchState({
         permittedDiversions: action.permittedSkillDiversions,
      });
   }
}
